import { chakra, HStack } from '@chakra-ui/react';
import React, { useCallback } from 'react';

import type { NFTTokenType, TokenType } from 'types/api/token';

import type { ButtonProps } from 'toolkit/chakra/button';
import { Button } from 'toolkit/chakra/button';
import { IconButton } from 'toolkit/chakra/icon-button';
import { Skeleton } from 'toolkit/chakra/skeleton';
import { Tag } from 'toolkit/chakra/tag';
import IconSvg from 'ui/shared/IconSvg';

interface Props<T> extends ButtonProps {
  isLoading?: boolean;
  filterName?: string;
  selectedValues?: Array<T>;
  placeholder: string;
  onRemoveTypeFilter: (nextValue: T) => void;
}

const SelectedValueTag = <T extends TokenType | NFTTokenType>({ label, handleClose }: { label: T; handleClose: (label: T) => void }) => {

  const onClose = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();

    handleClose(label);
  }, [ handleClose, label ]);;

  return (
    <Tag
      endElement={ (
        <IconButton _hover={{ opacity: 0.5 }} onClick={ onClose }>
          <IconSvg name="close" boxSize={ 4 } ml={ 1 }/>
        </IconButton>
      ) }
      variant="filter" size="lg">{ label }</Tag>
  );
};

const FilterButton = <T extends TokenType | NFTTokenType>({
  isLoading,
  filterName,
  selectedValues,
  placeholder,
  onRemoveTypeFilter,
  ...rest
}: Props<T>, ref: React.ForwardedRef<HTMLButtonElement>) => {

  if (isLoading) {
    return <Skeleton loading w={{ base: 9, lg: '78px' }} h={ 8 } borderRadius="base" flexShrink={ 0 }/>;
  }

  return (
    <Button
      ref={ ref }
      fontWeight="medium"
      gap="6px"
      variant="dropdown"
      fontStyle="text.sm"
      flexShrink={ 0 }
      pointerEvents="all"
      px={{ base: 1, lg: 3 }}
      { ...rest }
    >
      { filterName && <chakra.span fontWeight="normal" color="text.secondaryDarker">{ filterName }</chakra.span> }

      <HStack gap={ 1 }>
        { selectedValues?.length === 0 ?
          placeholder : (
            selectedValues?.map(label => <SelectedValueTag handleClose={ onRemoveTypeFilter } key={ label } label={ label }/>)
          ) }
      </HStack>
      <IconSvg
        _groupExpanded={{ transform: 'rotate(180deg)' }}
        name="arrows/down"
        boxSize={ 5 }
      />
    </Button>
  );
};

export default React.forwardRef(FilterButton) as <
  T extends TokenType | NFTTokenType,
>(
  props: Props<T> & { ref?: React.ForwardedRef<HTMLButtonElement> }
) => React.JSX.Element;
